import { b2AABB, b2OBB } from "./collision.js";
import { b2Mat22, b2Math, b2Vec2 } from "./common/math.js";
import { b2Settings } from "./common/settings.js";

export class b2FilterData {
  constructor() {
    this.maskBits = 65535;
    this.groupIndex = 0;
    this.categoryBits = 1;
  }

  Copy() {
    const filter = new b2FilterData();
    filter.categoryBits = this.categoryBits;
    filter.maskBits = this.maskBits;
    filter.groupIndex = this.groupIndex;
    return filter;
  }
}

export class b2MassData {
  constructor() {
    this.mass = 0;
    this.center = new b2Vec2(0, 0);
    this.I = 0;
  }
}

export class b2ShapeDef {
  constructor() {
    this.friction = 0.2;
    this.isSensor = false;
    this.density = 0;
    this.restitution = 0;
    this.userData = null;
    this.filter = new b2FilterData();
    this.type = -1;
  }
}

export class b2CircleDef extends b2ShapeDef {
  constructor() {
    super();
    this.radius = 1;
    this.localPosition = new b2Vec2(0, 0);
    this.type = b2Shape.e_circleShape;
  }
}

export class b2PolygonDef extends b2ShapeDef {
  static s_mat = new b2Mat22();

  constructor() {
    super();
    this.vertices = new Array(b2Settings.b2_maxPolygonVertices);
    this.vertexCount = 0;
    this.type = b2Shape.e_polygonShape;
    for (let i = 0; i < b2Settings.b2_maxPolygonVertices; i += 1) {
      this.vertices[i] = new b2Vec2();
    }
  }

  SetAsOrientedBox(hx, hy, center = null, angle = 0) {
    this.vertexCount = 4;
    this.vertices[0].Set(-hx, -hy);
    this.vertices[1].Set(hx, -hy);
    this.vertices[2].Set(hx, hy);
    this.vertices[3].Set(-hx, hy);
    if (center) {
      const rotation = b2PolygonDef.s_mat;
      rotation.Set(angle);
      for (let i = 0; i < this.vertexCount; i += 1) {
        const vertex = this.vertices[i];
        const x = center.x + (rotation.col1.x * vertex.x + rotation.col2.x * vertex.y);
        vertex.y = center.y + (rotation.col1.y * vertex.x + rotation.col2.y * vertex.y);
        vertex.x = x;
      }
    }
  }

  SetAsBox(hx, hy) {
    this.vertexCount = 4;
    this.vertices[0].Set(-hx, -hy);
    this.vertices[1].Set(hx, -hy);
    this.vertices[2].Set(hx, hy);
    this.vertices[3].Set(-hx, hy);
  }
}

export class b2Pair {
  static b2_nullPair = b2Settings.USHRT_MAX;
  static b2_nullProxy = b2Settings.USHRT_MAX;
  static b2_tableCapacity = b2Settings.b2_maxPairs;
  static b2_tableMask = b2Pair.b2_tableCapacity - 1;
  static e_pairBuffered = 1;
  static e_pairRemoved = 2;
  static e_pairFinal = 4;

  constructor() {
    this.proxyId1 = 0;
    this.userData = null;
    this.proxyId2 = 0;
    this.status = 0;
    this.next = 0;
  }

  SetBuffered() {
    this.status |= b2Pair.e_pairBuffered;
  }

  IsBuffered() {
    return (this.status & b2Pair.e_pairBuffered) === b2Pair.e_pairBuffered;
  }

  IsFinal() {
    return (this.status & b2Pair.e_pairFinal) === b2Pair.e_pairFinal;
  }

  ClearRemoved() {
    this.status &= ~b2Pair.e_pairRemoved;
  }

  SetFinal() {
    this.status |= b2Pair.e_pairFinal;
  }

  IsRemoved() {
    return (this.status & b2Pair.e_pairRemoved) === b2Pair.e_pairRemoved;
  }

  ClearBuffered() {
    this.status &= ~b2Pair.e_pairBuffered;
  }

  SetRemoved() {
    this.status |= b2Pair.e_pairRemoved;
  }
}

export class b2Shape {
  static s_proxyAABB = new b2AABB();
  static s_syncAABB = new b2AABB();
  static s_resetAABB = new b2AABB();
  static e_unknownShape = -1;
  static e_circleShape = 0;
  static e_polygonShape = 1;
  static e_shapeTypeCount = 2;

  constructor(def) {
    this.m_next = null;
    this.m_type = b2Shape.e_unknownShape;
    this.m_sweepRadius = 0;
    this.m_density = def.density;
    this.m_filter = def.filter.Copy();
    this.m_friction = def.friction;
    this.m_isSensor = def.isSensor;
    this.m_restitution = def.restitution;
    this.m_userData = def.userData;
    this.m_proxyId = b2Pair.b2_nullProxy;
    this.m_body = null;
  }

  static Destroy() {}

  static Create(def) {
    switch (def.type) {
      case b2Shape.e_circleShape:
        return new b2CircleShape(def);
      case b2Shape.e_polygonShape:
        return new b2PolygonShape(def);
      default:
        return null;
    }
  }

  SetUserData(userData) {
    this.m_userData = userData;
  }

  GetSweepRadius() {
    return this.m_sweepRadius;
  }

  GetNext() {
    return this.m_next;
  }

  ComputeSweptAABB() {}

  GetType() {
    return this.m_type;
  }

  GetRestitution() {
    return this.m_restitution;
  }

  GetFriction() {
    return this.m_friction;
  }

  GetFilterData() {
    return this.m_filter.Copy();
  }

  TestSegment() {
    return false;
  }

  RefilterProxy(broadPhase, transform) {
    if (this.m_proxyId === b2Pair.b2_nullProxy) {
      return;
    }
    broadPhase.DestroyProxy(this.m_proxyId);
    const aabb = b2Shape.s_resetAABB;
    this.ComputeAABB(aabb, transform);
    if (broadPhase.InRange(aabb)) {
      this.m_proxyId = broadPhase.CreateProxy(aabb, this);
    } else {
      this.m_proxyId = b2Pair.b2_nullProxy;
    }
  }

  SetFilterData(filter) {
    this.m_filter = filter.Copy();
  }

  GetUserData() {
    return this.m_userData;
  }

  Synchronize(broadPhase, transform1, transform2) {
    if (this.m_proxyId === b2Pair.b2_nullProxy) {
      return false;
    }
    const aabb = b2Shape.s_syncAABB;
    this.ComputeSweptAABB(aabb, transform1, transform2);
    if (broadPhase.InRange(aabb)) {
      broadPhase.MoveProxy(this.m_proxyId, aabb);
      return true;
    }
    return false;
  }

  ComputeMass() {}

  IsSensor() {
    return this.m_isSensor;
  }

  DestroyProxy(broadPhase) {
    if (this.m_proxyId !== b2Pair.b2_nullProxy) {
      broadPhase.DestroyProxy(this.m_proxyId);
      this.m_proxyId = b2Pair.b2_nullProxy;
    }
  }

  UpdateSweepRadius() {}

  ComputeAABB() {}

  GetBody() {
    return this.m_body;
  }

  CreateProxy(broadPhase, transform) {
    const aabb = b2Shape.s_proxyAABB;
    this.ComputeAABB(aabb, transform);
    if (broadPhase.InRange(aabb)) {
      this.m_proxyId = broadPhase.CreateProxy(aabb, this);
    } else {
      this.m_proxyId = b2Pair.b2_nullProxy;
    }
  }

  TestPoint() {
    return false;
  }
}

export class b2CircleShape extends b2Shape {
  constructor(def) {
    if (typeof def === "number") {
      const circleDef = new b2CircleDef();
      circleDef.radius = def;
      super(circleDef);
      this.m_localPosition = new b2Vec2();
      this.m_p = this.m_localPosition;
      this.m_radius = def;
      this.m_type = b2Shape.e_circleShape;
      return;
    }
    super(def);
    this.m_localPosition = new b2Vec2();
    this.m_p = this.m_localPosition;
    this.m_radius = 0;
    this.m_type = b2Shape.e_circleShape;
    this.m_localPosition.SetV(def.localPosition);
    this.m_radius = def.radius;
  }

  SetLocalPosition(position) {
    this.m_localPosition.SetV(position);
    this.m_p = this.m_localPosition;
  }

  TestSegment(transform, lambda, normal, segment, maxLambda) {
    const rotation = transform.R;
    const positionX = transform.position.x + (rotation.col1.x * this.m_localPosition.x + rotation.col2.x * this.m_localPosition.y);
    const positionY = transform.position.y + (rotation.col1.y * this.m_localPosition.x + rotation.col2.y * this.m_localPosition.y);
    const sX = segment.p1.x - positionX;
    const sY = segment.p1.y - positionY;
    const b = sX * sX + sY * sY - this.m_radius * this.m_radius;
    if (b < 0) {
      return false;
    }
    const rX = segment.p2.x - segment.p1.x;
    const rY = segment.p2.y - segment.p1.y;
    const c = sX * rX + sY * rY;
    const rr = rX * rX + rY * rY;
    const sigma = c * c - rr * b;
    if (sigma < 0 || rr < Number.MIN_VALUE) {
      return false;
    }
    let a = -(c + Math.sqrt(sigma));
    if (0 <= a && a <= maxLambda * rr) {
      a /= rr;
      lambda[0] = a;
      normal.x = sX + a * rX;
      normal.y = sY + a * rY;
      normal.Normalize();
      return true;
    }
    return false;
  }

  GetLocalPosition() {
    return this.m_localPosition;
  }

  GetRadius() {
    return this.m_radius;
  }

  ComputeSweptAABB(aabb, transform1, transform2) {
    let rotation = transform1.R;
    const x1 = transform1.position.x + (rotation.col1.x * this.m_localPosition.x + rotation.col2.x * this.m_localPosition.y);
    const y1 = transform1.position.y + (rotation.col1.y * this.m_localPosition.x + rotation.col2.y * this.m_localPosition.y);
    rotation = transform2.R;
    const x2 = transform2.position.x + (rotation.col1.x * this.m_localPosition.x + rotation.col2.x * this.m_localPosition.y);
    const y2 = transform2.position.y + (rotation.col1.y * this.m_localPosition.x + rotation.col2.y * this.m_localPosition.y);
    aabb.lowerBound.Set((x1 < x2 ? x1 : x2) - this.m_radius, (y1 < y2 ? y1 : y2) - this.m_radius);
    aabb.upperBound.Set((x1 > x2 ? x1 : x2) + this.m_radius, (y1 > y2 ? y1 : y2) + this.m_radius);
  }

  ComputeMass(massData) {
    massData.mass = this.m_density * b2Settings.b2_pi * this.m_radius * this.m_radius;
    massData.center.SetV(this.m_localPosition);
    massData.I = massData.mass * (0.5 * this.m_radius * this.m_radius + (this.m_localPosition.x * this.m_localPosition.x + this.m_localPosition.y * this.m_localPosition.y));
  }

  UpdateSweepRadius(center) {
    let dx = this.m_localPosition.x - center.x;
    let dy = this.m_localPosition.y - center.y;
    dx = Math.sqrt(dx * dx + dy * dy);
    this.m_sweepRadius = dx + this.m_radius - b2Settings.b2_toiSlop;
  }

  ComputeAABB(aabb, transform) {
    const rotation = transform.R;
    const x = transform.position.x + (rotation.col1.x * this.m_localPosition.x + rotation.col2.x * this.m_localPosition.y);
    const y = transform.position.y + (rotation.col1.y * this.m_localPosition.x + rotation.col2.y * this.m_localPosition.y);
    aabb.lowerBound.Set(x - this.m_radius, y - this.m_radius);
    aabb.upperBound.Set(x + this.m_radius, y + this.m_radius);
  }

  TestPoint(transform, point) {
    const rotation = transform.R;
    let dx = transform.position.x + (rotation.col1.x * this.m_localPosition.x + rotation.col2.x * this.m_localPosition.y);
    let dy = transform.position.y + (rotation.col1.y * this.m_localPosition.x + rotation.col2.y * this.m_localPosition.y);
    dx = point.x - dx;
    dy = point.y - dy;
    return dx * dx + dy * dy <= this.m_radius * this.m_radius;
  }
}

export class b2PolygonShape extends b2Shape {
  static s_computeMat = new b2Mat22();
  static s_sweptAABB1 = new b2AABB();
  static s_sweptAABB2 = new b2AABB();

  constructor(def) {
    const polygonDef = def ?? new b2PolygonDef();
    super(polygonDef);
    this.m_coreVertices = new Array(b2Settings.b2_maxPolygonVertices);
    this.m_vertices = new Array(b2Settings.b2_maxPolygonVertices);
    this.s_supportVec = new b2Vec2();
    this.m_centroid = new b2Vec2();
    this.m_normals = new Array(b2Settings.b2_maxPolygonVertices);
    this.m_obb = new b2OBB();
    this.m_vertexCount = polygonDef.vertexCount ?? 0;
    this.m_type = b2Shape.e_polygonShape;
    if (this.m_vertexCount > 0) {
      this.SetAsArray(polygonDef.vertices, this.m_vertexCount);
    }
  }

  SetAsArray(vertices, vertexCount = vertices.length) {
    this.m_vertexCount = vertexCount;
    for (let i = 0; i < this.m_vertexCount; i += 1) {
      this.m_vertices[i] = vertices[i].Copy();
    }
    for (let i = this.m_vertexCount; i < b2Settings.b2_maxPolygonVertices; i += 1) {
      this.m_vertices[i] = this.m_vertices[i] ?? new b2Vec2();
      this.m_normals[i] = this.m_normals[i] ?? new b2Vec2();
      this.m_coreVertices[i] = this.m_coreVertices[i] ?? new b2Vec2();
    }
    for (let i = 0; i < this.m_vertexCount; i += 1) {
      const i1 = i;
      const i2 = i + 1 < this.m_vertexCount ? i + 1 : 0;
      const edgeX = this.m_vertices[i2].x - this.m_vertices[i1].x;
      const edgeY = this.m_vertices[i2].y - this.m_vertices[i1].y;
      const length = Math.sqrt(edgeX * edgeX + edgeY * edgeY);
      this.m_normals[i] = new b2Vec2(edgeY / length, -edgeX / length);
    }
    this.m_centroid = b2PolygonShape.ComputeCentroid(this.m_vertices, this.m_vertexCount);
    b2PolygonShape.ComputeOBB(this.m_obb, this.m_vertices, this.m_vertexCount);
    for (let i = 0; i < this.m_vertexCount; i += 1) {
      const i1 = i - 1 >= 0 ? i - 1 : this.m_vertexCount - 1;
      const i2 = i;
      const n1X = this.m_normals[i1].x;
      const n1Y = this.m_normals[i1].y;
      const n2X = this.m_normals[i2].x;
      const n2Y = this.m_normals[i2].y;
      const dX = this.m_vertices[i].x - this.m_centroid.x;
      const dY = this.m_vertices[i].y - this.m_centroid.y;
      const d1 = n1X * dX + n1Y * dY - b2Settings.b2_toiSlop;
      const d2 = n2X * dX + n2Y * dY - b2Settings.b2_toiSlop;
      const inv = 1 / (n1X * n2Y - n1Y * n2X);
      this.m_coreVertices[i] = new b2Vec2(inv * (n2Y * d1 - n1Y * d2) + this.m_centroid.x, inv * (n1X * d2 - n2X * d1) + this.m_centroid.y);
    }
  }

  static ComputeCentroid(vertices, vertexCount) {
    const center = new b2Vec2();
    let area = 0;
    const inv3 = 1 / 3;
    for (let i = 0; i < vertexCount; i += 1) {
      const p1 = vertices[i];
      const p2 = i + 1 < vertexCount ? vertices[i + 1] : vertices[0];
      const e1X = p1.x;
      const e1Y = p1.y;
      const e2X = p2.x;
      const e2Y = p2.y;
      const cross = e1X * e2Y - e1Y * e2X;
      const triangleArea = 0.5 * cross;
      area += triangleArea;
      center.x += triangleArea * inv3 * (p1.x + p2.x);
      center.y += triangleArea * inv3 * (p1.y + p2.y);
    }
    center.x *= 1 / area;
    center.y *= 1 / area;
    return center;
  }

  static ComputeOBB(obb, vertices, vertexCount) {
    const p = new Array(b2Settings.b2_maxPolygonVertices + 1);
    for (let i = 0; i < vertexCount; i += 1) {
      p[i] = vertices[i];
    }
    p[vertexCount] = p[0];
    let minArea = Number.MAX_VALUE;
    for (let i = 1; i <= vertexCount; i += 1) {
      const root = p[i - 1];
      let ux = p[i].x - root.x;
      let uy = p[i].y - root.y;
      const length = Math.sqrt(ux * ux + uy * uy);
      ux /= length;
      uy /= length;
      const vx = -uy;
      const vy = ux;
      let lowerX = Number.MAX_VALUE;
      let lowerY = Number.MAX_VALUE;
      let upperX = -Number.MAX_VALUE;
      let upperY = -Number.MAX_VALUE;
      for (let j = 0; j < vertexCount; j += 1) {
        const dX = p[j].x - root.x;
        const dY = p[j].y - root.y;
        const rX = ux * dX + uy * dY;
        const rY = vx * dX + vy * dY;
        if (rX < lowerX) {
          lowerX = rX;
        }
        if (rY < lowerY) {
          lowerY = rY;
        }
        if (rX > upperX) {
          upperX = rX;
        }
        if (rY > upperY) {
          upperY = rY;
        }
      }
      const area = (upperX - lowerX) * (upperY - lowerY);
      if (area < 0.95 * minArea) {
        minArea = area;
        obb.R.col1.x = ux;
        obb.R.col1.y = uy;
        obb.R.col2.x = vx;
        obb.R.col2.y = vy;
        const centerX = 0.5 * (lowerX + upperX);
        const centerY = 0.5 * (lowerY + upperY);
        obb.center.x = root.x + (obb.R.col1.x * centerX + obb.R.col2.x * centerY);
        obb.center.y = root.y + (obb.R.col1.y * centerX + obb.R.col2.y * centerY);
        obb.extents.x = 0.5 * (upperX - lowerX);
        obb.extents.y = 0.5 * (upperY - lowerY);
      }
    }
  }

  ComputeSweptAABB(aabb, transform1, transform2) {
    const aabb1 = b2PolygonShape.s_sweptAABB1;
    const aabb2 = b2PolygonShape.s_sweptAABB2;
    this.ComputeAABB(aabb1, transform1);
    this.ComputeAABB(aabb2, transform2);
    aabb.lowerBound.Set(
      aabb1.lowerBound.x < aabb2.lowerBound.x ? aabb1.lowerBound.x : aabb2.lowerBound.x,
      aabb1.lowerBound.y < aabb2.lowerBound.y ? aabb1.lowerBound.y : aabb2.lowerBound.y,
    );
    aabb.upperBound.Set(
      aabb1.upperBound.x > aabb2.upperBound.x ? aabb1.upperBound.x : aabb2.upperBound.x,
      aabb1.upperBound.y > aabb2.upperBound.y ? aabb1.upperBound.y : aabb2.upperBound.y,
    );
  }

  GetVertices() {
    return this.m_vertices;
  }

  GetCoreVertices() {
    return this.m_coreVertices;
  }

  GetCentroid() {
    return this.m_centroid;
  }

  GetOBB() {
    return this.m_obb;
  }

  GetFirstVertex(transform) {
    return b2Math.b2MulX(transform, this.m_coreVertices[0]);
  }

  Centroid(transform) {
    return b2Math.b2MulX(transform, this.m_centroid);
  }

  TestSegment(transform, lambda, normal, segment, maxLambda) {
    let lower = 0;
    let upper = maxLambda;
    let pX = segment.p1.x - transform.position.x;
    let pY = segment.p1.y - transform.position.y;
    let rotation = transform.R;
    const p1X = pX * rotation.col1.x + pY * rotation.col1.y;
    const p1Y = pX * rotation.col2.x + pY * rotation.col2.y;
    pX = segment.p2.x - transform.position.x;
    pY = segment.p2.y - transform.position.y;
    rotation = transform.R;
    const p2X = pX * rotation.col1.x + pY * rotation.col1.y;
    const p2Y = pX * rotation.col2.x + pY * rotation.col2.y;
    const dX = p2X - p1X;
    const dY = p2Y - p1Y;
    let index = -1;
    for (let i = 0; i < this.m_vertexCount; i += 1) {
      let tX = this.m_vertices[i].x - p1X;
      let tY = this.m_vertices[i].y - p1Y;
      const vertexNormal = this.m_normals[i];
      const numerator = vertexNormal.x * tX + vertexNormal.y * tY;
      const denominator = vertexNormal.x * dX + vertexNormal.y * dY;
      if (denominator < 0 && numerator < lower * denominator) {
        lower = numerator / denominator;
        index = i;
      } else if (denominator > 0 && numerator < upper * denominator) {
        upper = numerator / denominator;
      }
      if (upper < lower) {
        return false;
      }
    }
    if (index >= 0) {
      lambda[0] = lower;
      rotation = transform.R;
      const vertexNormal = this.m_normals[index];
      normal.x = rotation.col1.x * vertexNormal.x + rotation.col2.x * vertexNormal.y;
      normal.y = rotation.col1.y * vertexNormal.x + rotation.col2.y * vertexNormal.y;
      return true;
    }
    return false;
  }

  ComputeMass(massData) {
    let centerX = 0;
    let centerY = 0;
    let area = 0;
    let inertia = 0;
    const inv3 = 1 / 3;
    for (let i = 0; i < this.m_vertexCount; i += 1) {
      const p1 = this.m_vertices[i];
      const p2 = i + 1 < this.m_vertexCount ? this.m_vertices[i + 1] : this.m_vertices[0];
      const e1X = p1.x;
      const e1Y = p1.y;
      const e2X = p2.x;
      const e2Y = p2.y;
      const D = e1X * e2Y - e1Y * e2X;
      const triangleArea = 0.5 * D;
      area += triangleArea;
      centerX += triangleArea * inv3 * (p1.x + p2.x);
      centerY += triangleArea * inv3 * (p1.y + p2.y);
      const intX = inv3 * (0.25 * (e1X * e1X + e2X * e1X + e2X * e2X));
      const intY = inv3 * (0.25 * (e1Y * e1Y + e2Y * e1Y + e2Y * e2Y));
      inertia += D * (intX + intY);
    }
    massData.mass = this.m_density * area;
    centerX *= 1 / area;
    centerY *= 1 / area;
    massData.center.Set(centerX, centerY);
    massData.I = this.m_density * inertia;
  }

  GetNormals() {
    return this.m_normals;
  }

  Support(transform, dX, dY) {
    const rotation = transform.R;
    const localX = dX * rotation.col1.x + dY * rotation.col1.y;
    const localY = dX * rotation.col2.x + dY * rotation.col2.y;
    let bestIndex = 0;
    let vertex = this.m_coreVertices[0];
    let bestValue = vertex.x * localX + vertex.y * localY;
    for (let i = 1; i < this.m_vertexCount; i += 1) {
      vertex = this.m_coreVertices[i];
      const value = vertex.x * localX + vertex.y * localY;
      if (value > bestValue) {
        bestIndex = i;
        bestValue = value;
      }
    }
    vertex = this.m_coreVertices[bestIndex];
    this.s_supportVec.x = transform.position.x + (rotation.col1.x * vertex.x + rotation.col2.x * vertex.y);
    this.s_supportVec.y = transform.position.y + (rotation.col1.y * vertex.x + rotation.col2.y * vertex.y);
    return this.s_supportVec;
  }

  GetVertexCount() {
    return this.m_vertexCount;
  }

  ComputeAABB(aabb, transform) {
    const mat = b2PolygonShape.s_computeMat;
    const rotation = transform.R;
    let obbR = this.m_obb.R.col1;
    mat.col1.x = rotation.col1.x * obbR.x + rotation.col2.x * obbR.y;
    mat.col1.y = rotation.col1.y * obbR.x + rotation.col2.y * obbR.y;
    obbR = this.m_obb.R.col2;
    mat.col2.x = rotation.col1.x * obbR.x + rotation.col2.x * obbR.y;
    mat.col2.y = rotation.col1.y * obbR.x + rotation.col2.y * obbR.y;
    mat.Abs();
    const extents = this.m_obb.extents;
    const hX = mat.col1.x * extents.x + mat.col2.x * extents.y;
    const hY = mat.col1.y * extents.x + mat.col2.y * extents.y;
    const center = this.m_obb.center;
    const centerX = transform.position.x + (rotation.col1.x * center.x + rotation.col2.x * center.y);
    const centerY = transform.position.y + (rotation.col1.y * center.x + rotation.col2.y * center.y);
    aabb.lowerBound.Set(centerX - hX, centerY - hY);
    aabb.upperBound.Set(centerX + hX, centerY + hY);
  }

  UpdateSweepRadius(center) {
    this.m_sweepRadius = 0;
    for (let i = 0; i < this.m_vertexCount; i += 1) {
      const core = this.m_coreVertices[i];
      let dX = core.x - center.x;
      let dY = core.y - center.y;
      dX = Math.sqrt(dX * dX + dY * dY);
      if (dX > this.m_sweepRadius) {
        this.m_sweepRadius = dX;
      }
    }
  }

  TestPoint(transform, point) {
    const rotation = transform.R;
    let pX = point.x - transform.position.x;
    let pY = point.y - transform.position.y;
    const localX = pX * rotation.col1.x + pY * rotation.col1.y;
    const localY = pX * rotation.col2.x + pY * rotation.col2.y;
    for (let i = 0; i < this.m_vertexCount; i += 1) {
      const vertex = this.m_vertices[i];
      pX = localX - vertex.x;
      pY = localY - vertex.y;
      const normal = this.m_normals[i];
      const dot = normal.x * pX + normal.y * pY;
      if (dot > 0) {
        return false;
      }
    }
    return true;
  }
}
